import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import * as dfd from "danfojs";
import {
    Bar,
    CartesianGrid,
    Cell,
    ComposedChart,
    Label,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { Badge } from "@/client/components/base/badge";
import { Button } from "@/client/components/base/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/client/components/base/card";
import { api } from "@/client/lib";

const BIN_SIZE = 1;
const TRADING_DAYS_PER_YEAR = 252;
const WEEKLY_EXPIRY_START = new Date(2019, 1, 13);
const POSITIVE_COLOR = "#10b981";
const NEGATIVE_COLOR = "#ef4444";
const ACCENT_COLOR = "#6366f1";

type RawRow = {
    timestamp: Date;
    high: number;
    low: number;
    close: number;
    volume: number;
    oi: number | null;
};

type HistogramBin = {
    bin: number;
    label: string;
    count: number;
};

type Analysis = {
    histogram: HistogramBin[];
    latestReturnPct: number;
    latestClose: { price: number; date: Date };
    lastExpiry: { price: number; date: Date };
    currentReturn: number;
    volatility: number;
    avgReturn: number;
    maxGain: number;
    maxLoss: number;
    totalExpiries: number;
    positive: number;
    negative: number;
    positivePct: number;
};

const fmtPrice = (value: number) =>
    value.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });

const fmtNumber = (value: number, digits = 2) =>
    value.toLocaleString("en-IN", { minimumFractionDigits: digits, maximumFractionDigits: digits });

const fmtSignedPct = (value: number, digits = 2) => `${value >= 0 ? "+" : ""}${fmtNumber(value, digits)}%`;

const fmtDate = (date: Date) =>
    date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });

function parseDateString(value: string): Date {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (match) {
        return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    }
    return new Date(value);
}

function toDate(value: Date | string): Date {
    return typeof value === "string" ? parseDateString(value) : value;
}

function lastCloseAtOrBefore(sortedDates: number[], sortedCloses: number[], target: number): number {
    let low = 0;
    let high = sortedDates.length - 1;
    let answer = -1;
    while (low <= high) {
        const mid = (low + high) >> 1;
        if (sortedDates[mid] <= target) {
            answer = mid;
            low = mid + 1;
        } else {
            high = mid - 1;
        }
    }
    return answer >= 0 ? sortedCloses[answer] : Number.NaN;
}

function computeMonthlyExpiries(expiries: Date[]): Date[] {
    const lastByMonth = new Map<string, Date>();
    for (const expiry of expiries) {
        const key = `${expiry.getFullYear()}-${expiry.getMonth()}`;
        const current = lastByMonth.get(key);
        if (!current || expiry.getTime() > current.getTime()) {
            lastByMonth.set(key, expiry);
        }
    }
    return [...lastByMonth.values()].sort((a, b) => a.getTime() - b.getTime());
}

function buildHistogram(returnPcts: number[]): HistogramBin[] {
    if (returnPcts.length === 0) return [];

    const bins = returnPcts.map((value) => Math.floor(value / BIN_SIZE));
    const countByBin = new Map<number, number>();
    try {
        const result = new dfd.Series(bins).valueCounts();
        if (result["value"] !== undefined) {
            const binValues = result["value"].values as number[];
            const binCounts = result["count"].values as number[];
            for (let i = 0; i < binValues.length; i++) {
                countByBin.set(Number(binValues[i]), Number(binCounts[i]));
            }
        } else {
            const binValues = result.index as number[];
            const binCounts = result.values as number[];
            for (let i = 0; i < binValues.length; i++) {
                countByBin.set(Number(binValues[i]), Number(binCounts[i]));
            }
        }
    } catch {
        for (const bin of bins) {
            countByBin.set(bin, (countByBin.get(bin) ?? 0) + 1);
        }
    }

    const minBin = Math.min(...bins);
    const maxBin = Math.max(...bins);
    const histogram: HistogramBin[] = [];
    for (let bin = minBin; bin <= maxBin; bin++) {
        histogram.push({
            bin: bin * BIN_SIZE,
            label: `${bin * BIN_SIZE}% to ${(bin + 1) * BIN_SIZE}%`,
            count: countByBin.get(bin) ?? 0,
        });
    }
    return histogram;
}

export default function ReturnDistributionPage() {
    const { expiryType, underlying } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [rawData, setRawData] = useState<RawRow[]>([]);
    const [nseExpiries, setNseExpiries] = useState<string[]>([]);

    const fetchData = useCallback(() => {
        if (!expiryType || !underlying) return;
        setLoading(true);
        setError(null);
        Promise.all([api.raw.data({ symbol: underlying })({ interval: "1d" }).get(), api.nse.expiries.get()])
            .then(([rawRes, expiryRes]) => {
                if (!rawRes.error && rawRes.data) {
                    setRawData(rawRes.data);
                }
                if (!expiryRes.error && expiryRes.data) {
                    setNseExpiries(expiryRes.data);
                }
                const message = rawRes.error?.message ?? expiryRes.error?.message;
                if (message) {
                    setError(message);
                }
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : "Failed to load data");
            })
            .finally(() => setLoading(false));
    }, [expiryType, underlying]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const analysis = useMemo<Analysis | null>(() => {
        if (rawData.length === 0 || nseExpiries.length === 0) return null;

        const sortedRaw = [...rawData]
            .map((row) => ({ ...row, date: toDate(row.timestamp) }))
            .sort((a, b) => a.date.getTime() - b.date.getTime());
        const sortedDates = sortedRaw.map((row) => row.date.getTime());
        const sortedCloses = sortedRaw.map((row) => row.close);

        const dailyReturns: number[] = [];
        for (let i = 1; i < sortedCloses.length; i++) {
            dailyReturns.push((sortedCloses[i] - sortedCloses[i - 1]) / sortedCloses[i - 1]);
        }
        const dailyRetStd = Number(new dfd.Series(dailyReturns).std()) || 0;
        const volatility = dailyRetStd * Math.sqrt(TRADING_DAYS_PER_YEAR) * 100;

        const allExpiries = nseExpiries
            .map(parseDateString)
            .filter((date) => !Number.isNaN(date.getTime()))
            .sort((a, b) => a.getTime() - b.getTime());

        const monthlyExpiries = computeMonthlyExpiries(allExpiries);
        const selectedExpiries =
            (expiryType ?? "").toLowerCase() === "monthly"
                ? monthlyExpiries
                : allExpiries.filter((date) => date.getTime() >= WEEKLY_EXPIRY_START.getTime());

        const expiryPoints = selectedExpiries
            .map((expiry) => ({
                date: expiry,
                close: lastCloseAtOrBefore(sortedDates, sortedCloses, expiry.getTime()),
            }))
            .filter((point) => Number.isFinite(point.close));

        const latestCloseRow = sortedRaw[sortedRaw.length - 1];
        const latestCloseTime = latestCloseRow.date.getTime();

        const completedExpiryPoints = expiryPoints.filter((point) => point.date.getTime() <= latestCloseTime);

        const returnPcts: number[] = [];
        for (let i = 1; i < completedExpiryPoints.length; i++) {
            const prev = completedExpiryPoints[i - 1];
            const current = completedExpiryPoints[i];
            returnPcts.push(((current.close - prev.close) / prev.close) * 100);
        }

        const lastExpiryPoint = completedExpiryPoints[completedExpiryPoints.length - 1];
        const latestReturnPct = returnPcts[returnPcts.length - 1] ?? 0;
        const currentReturn =
            lastExpiryPoint && lastExpiryPoint.close > 0
                ? ((latestCloseRow.close - lastExpiryPoint.close) / lastExpiryPoint.close) * 100
                : 0;

        const returnSeries = new dfd.Series(returnPcts);
        const avgReturn = returnPcts.length > 0 ? Number(returnSeries.mean()) || 0 : 0;
        const maxGain = returnPcts.length > 0 ? Number(returnSeries.max()) || 0 : 0;
        const maxLoss = returnPcts.length > 0 ? Number(returnSeries.min()) || 0 : 0;
        const positive = returnPcts.filter((value) => value > 0).length;
        const negative = returnPcts.filter((value) => value < 0).length;

        return {
            histogram: buildHistogram(returnPcts),
            latestReturnPct,
            latestClose: {
                price: latestCloseRow.close,
                date: latestCloseRow.date,
            },
            lastExpiry: lastExpiryPoint
                ? { price: lastExpiryPoint.close, date: lastExpiryPoint.date }
                : { price: 0, date: latestCloseRow.date },
            currentReturn,
            volatility,
            avgReturn,
            maxGain,
            maxLoss,
            totalExpiries: completedExpiryPoints.length,
            positive,
            negative,
            positivePct: returnPcts.length > 0 ? (positive / returnPcts.length) * 100 : 0,
        };
    }, [rawData, nseExpiries, expiryType]);

    if (loading) {
        return <div className="flex h-64 items-center justify-center text-muted-foreground">Loading…</div>;
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center gap-3 p-8 text-center">
                <p className="text-sm text-destructive">{error}</p>
                <Button variant="outline" onClick={fetchData}>
                    Retry
                </Button>
            </div>
        );
    }

    if (!analysis) {
        return <div className="flex h-64 items-center justify-center text-muted-foreground">No data available</div>;
    }

    const isMonthly = (expiryType ?? "").toLowerCase() === "monthly";
    const binStart = analysis.histogram[0]?.bin ?? 0;
    const binEnd = (analysis.histogram[analysis.histogram.length - 1]?.bin ?? 0) + BIN_SIZE;

    return (
        <div className="flex flex-col gap-4 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-lg font-semibold">{underlying} Return Distribution</h1>
                    <p className="text-sm text-muted-foreground">
                        {isMonthly ? "Monthly" : "Weekly"} expiry-to-expiry returns with {BIN_SIZE}% bins
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={fetchData}>
                        Refresh
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <PriceCard
                    title="Last Expiry Close"
                    price={analysis.lastExpiry.price}
                    date={analysis.lastExpiry.date}
                />
                <PriceCard title="Latest Close" price={analysis.latestClose.price} date={analysis.latestClose.date} />

                <MetricCard
                    label="Current Return (Last Expiry → Latest)"
                    value={fmtSignedPct(analysis.currentReturn)}
                    hint={`${fmtDate(analysis.lastExpiry.date)} → ${fmtDate(analysis.latestClose.date)}`}
                    tone={analysis.currentReturn >= 0 ? "positive" : "negative"}
                />
                <MetricCard
                    label="Stock Volatility"
                    value={`${fmtNumber(analysis.volatility)}%`}
                    hint="Annualised, daily close-to-close"
                />
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Expiry Metrics</CardTitle>
                    <CardDescription>Summary across {analysis.totalExpiries} expiries</CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
                    <MetricCard label="Max Gain" value={fmtSignedPct(analysis.maxGain)} tone="positive" />
                    <MetricCard label="Max Loss" value={fmtSignedPct(analysis.maxLoss)} tone="negative" />
                    <MetricCard
                        label="Avg Expiry Return"
                        value={fmtSignedPct(analysis.avgReturn)}
                        tone={analysis.avgReturn >= 0 ? "positive" : "negative"}
                    />
                    <MetricCard label="Total Expiries" value={String(analysis.totalExpiries)} />
                    <MetricCard
                        label="Positive Expiries"
                        value={String(analysis.positive)}
                        hint={`${fmtNumber(analysis.positivePct, 0)}% of periods`}
                    />
                    <MetricCard label="Negative Expiries" value={String(analysis.negative)} />
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="flex-row items-center justify-between space-y-0">
                    <div className="space-y-1">
                        <CardTitle className="text-base">Expiry-to-Expiry Return Histogram</CardTitle>
                        <CardDescription>
                            {analysis.totalExpiries} expiries · {analysis.positive} positive / {analysis.negative}{" "}
                            negative · bin width {BIN_SIZE}%
                        </CardDescription>
                    </div>
                    <Badge variant="secondary">
                        Latest Close {fmtPrice(analysis.latestClose.price)} · {fmtDate(analysis.latestClose.date)}
                    </Badge>
                </CardHeader>
                <CardContent>
                    {analysis.histogram.length === 0 ? (
                        <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
                            Not enough expiry data to compute returns
                        </div>
                    ) : (
                        <div className="h-80 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart
                                    data={analysis.histogram}
                                    margin={{ top: 24, right: 16, left: 0, bottom: 8 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis
                                        dataKey="bin"
                                        type="number"
                                        domain={[binStart, binEnd]}
                                        tickCount={Math.min(20, analysis.histogram.length + 1)}
                                        tickFormatter={(value: number) => `${value}%`}
                                        tickLine={false}
                                        axisLine={false}
                                    />
                                    <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={32} />
                                    <Tooltip content={<HistogramTooltip />} cursor={{ fill: "rgba(0,0,0,0.05)" }} />
                                    <Bar dataKey="count" name="Expiries" radius={[2, 2, 0, 0]} maxBarSize={48}>
                                        {analysis.histogram.map((bin) => (
                                            <Cell key={bin.bin} fill={bin.bin >= 0 ? POSITIVE_COLOR : NEGATIVE_COLOR} />
                                        ))}
                                    </Bar>
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

type Tone = "positive" | "negative" | "neutral";

function PriceCard({ title, price, date }: { title: string; price: number; date: Date }) {
    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
            </CardHeader>
            <CardContent>
                <p className="text-2xl font-semibold tabular-nums">{fmtPrice(price)}</p>
                <p className="mt-1 text-xs text-muted-foreground">{fmtDate(date)}</p>
            </CardContent>
        </Card>
    );
}

function MetricCard({
    label,
    value,
    hint,
    tone = "neutral",
}: {
    label: string;
    value: string;
    hint?: string;
    tone?: Tone;
}) {
    const toneClass =
        tone === "positive" ? "text-emerald-600" : tone === "negative" ? "text-red-600" : "text-foreground";
    return (
        <div className="flex flex-col gap-1 rounded-lg border bg-card p-3">
            <span className="text-xs text-muted-foreground">{label}</span>
            <span className={`text-lg font-semibold tabular-nums ${toneClass}`}>{value}</span>
            {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
        </div>
    );
}

function HistogramTooltip({ active, payload }: { active?: boolean; payload?: { payload: HistogramBin }[] }) {
    if (!active || !payload?.length) return null;
    const bin = payload[0].payload;
    return (
        <div className="rounded-lg border bg-background px-2.5 py-1.5 text-xs shadow-sm">
            <p className="font-medium text-foreground">{bin.label}</p>
            <p className="text-muted-foreground">{bin.count} expiries</p>
        </div>
    );
}
